import React from "react";
import { estimateBJTScore, getAbilityLabel, gaussian } from "../utils/irtScore";

const W = 208, H = 110;
const PAD_L = 18, PAD_R = 18, PAD_T = 24, PAD_B = 28;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
const MIN_S = 200, MAX_S = 700;
const TICKS = [200, 300, 400, 500, 600, 700];

function sx(score) {
  return PAD_L + ((score - MIN_S) / (MAX_S - MIN_S)) * PLOT_W;
}

function curvePoints() {
  const pts = [];
  for (let s = MIN_S; s <= MAX_S; s += 4) {
    const x = sx(s);
    const y = PAD_T + PLOT_H - gaussian(s) * PLOT_H * 0.88;
    pts.push([x, y]);
  }
  return pts;
}

export default function ScorePanel({ questions, answers }) {
  const score = estimateBJTScore(questions, answers);
  const label = getAbilityLabel(score);
  const pts = curvePoints();
  const polyline = pts.map(([x, y]) => `${x},${y}`).join(" ");

  const dotX = sx(score);
  const dotY = PAD_T + PLOT_H - gaussian(score) * PLOT_H * 0.88;

  // Filled area: curve + baseline corners
  const fillPath =
    `M ${PAD_L},${PAD_T + PLOT_H} ` +
    pts.map(([x, y]) => `L ${x},${y}`).join(" ") +
    ` L ${PAD_L + PLOT_W},${PAD_T + PLOT_H} Z`;

  return (
    <div className="mt-2 bg-gray-950 rounded-xl p-3 flex flex-col items-center gap-1">
      {/* Score label */}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-amber-400 leading-none">{score}</span>
        <span className="text-xs text-gray-400">{label}</span>
      </div>

      {/* SVG curve */}
      <svg width={W} height={H} className="overflow-visible">
        <defs>
          <linearGradient id="fillGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.03" />
          </linearGradient>
          {/* Glow filter for dot */}
          <filter id="glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Baseline */}
        <line
          x1={PAD_L} y1={PAD_T + PLOT_H}
          x2={PAD_L + PLOT_W} y2={PAD_T + PLOT_H}
          stroke="#374151" strokeWidth="1"
        />

        {/* Fill under curve */}
        <path d={fillPath} fill="url(#fillGrad)" />

        {/* Curve line */}
        <polyline
          points={polyline}
          fill="none"
          stroke="#818cf8"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />

        {/* Vertical drop line from dot to baseline */}
        <line
          x1={dotX} y1={dotY + 5}
          x2={dotX} y2={PAD_T + PLOT_H}
          stroke="#fbbf24"
          strokeWidth="1"
          strokeDasharray="2,2"
          opacity="0.6"
        />

        {/* Glowing dot */}
        <circle cx={dotX} cy={dotY} r="9" fill="#fbbf24" opacity="0.25" filter="url(#glow)" />
        <circle cx={dotX} cy={dotY} r="5" fill="#f59e0b" filter="url(#glow)" />
        <circle cx={dotX} cy={dotY} r="2.5" fill="#fff9" />

        {/* Score callout above dot */}
        <text
          x={dotX}
          y={dotY - 12}
          textAnchor="middle"
          fontSize="8.5"
          fontWeight="700"
          fill="#fbbf24"
        >
          {score}
        </text>

        {/* X-axis ticks */}
        {TICKS.map((t) => (
          <g key={t}>
            <line
              x1={sx(t)} y1={PAD_T + PLOT_H}
              x2={sx(t)} y2={PAD_T + PLOT_H + 3}
              stroke="#4b5563" strokeWidth="1"
            />
            <text
              x={sx(t)}
              y={H - 4}
              textAnchor="middle"
              fontSize="7"
              fill="#6b7280"
            >
              {t}
            </text>
          </g>
        ))}
      </svg>

      <p className="text-[10px] text-gray-500 text-center leading-tight">
        基于 IRT 模型估算 · 仅供参考
      </p>
    </div>
  );
}
